import { t as tr } from '@hana/i18n';
import './CostJobs.i18n';
import { Fragment, useMemo, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { IconArrowRight, IconChevronDown, IconChevronRight, IconHelp, IconRefresh } from '@tabler/icons-react';
import { useToast } from '@hana/ui';
import { COSTING_JOBS } from './costingJobs';
import { calcCosting } from './costing';
import type { CostingCalc } from './costingTypes';
import { f1, norm } from './view';
import { useT } from '@hana/i18n';
import s from './costJobs.module.css';
import './finance.i18n';

// Mint-Cost › tab Costing theo Job (1536): lọc nhanh · bảng 9 cột, bung theo dịch vụ · chân bảng. Mọi số là TIỀN → màn đã gói sau quyền `money`. Chỉ đọc.

const STATUS = {
  draft: { k: 'Nháp', tone: 'gray' },
  pend: { k: 'Chờ duyệt', tone: 'warn' },
  appr: { k: 'Đã duyệt', tone: 'ok' },
  lockp: { k: 'Đã khoá kỳ', tone: 'violet' },
} as const;

/** Rộng cột theo board (CW 1240): 230 · 86 · 94 · 94 · 78 · 100 · 88 · 176 · còn lại. */
const COLS = [
  { k: 'Job · khách', w: 230 },
  { k: 'Bán', w: 86, right: true },
  { k: 'Mua dự kiến', w: 94, right: true },
  { k: 'Mua thực tế', w: 94, right: true },
  { k: 'Chênh', w: 78, right: true },
  { k: 'GP quản trị', w: 100, right: true },
  { k: 'GP kế toán', w: 88, right: true },
  { k: 'Cảnh báo', w: 176 },
  { k: 'Trạng thái', w: 294 },
] as const;
const TOTAL = COLS.reduce((a, c) => a + c.w, 0);

const HINT_WARN =
  'Chấm đỏ: lỗ thực tế · dòng trùng · trễ duyệt costing. Chấm cam: dự kiến lỗ · bán dưới giá mua · thiếu cặp bán–mua · lệch DN / HĐ mua vào · tỷ giá · sắp trễ duyệt (còn ≤ 2 ngày). Hạn duyệt tính từ mốc Finish job: thuần cước 5, có plus services 30 ngày làm việc (WorkCalendar). Bấm dòng để mở chi tiết costing.';

type Seg = 'all' | 'warn' | 'loss' | 'late';
const var_ = (c: CostingCalc) => c.cost - c.qCost;
const signed = (v: number) => (v > 0.04 ? '+' : '') + f1(v);
const varTone = (v: number, hi: number) => (v > hi ? s.err : v > 0.04 ? s.warn : s.ok);
const isLoss = (c: CostingCalc) => (c.fin && c.gp < 0) || (!c.fin && c.proj < 0);
const isLate = (c: CostingCalc) => !!(c.deadline && c.deadline.lv);
const dlChip = (c: CostingCalc) => (c.deadline!.lv === 'r' ? `Trễ ${-c.deadline!.left} ngày` : `Sắp trễ · còn ${c.deadline!.left} ngày`);

export function CostJobs({ q }: { q: string }) {
  const toast = useToast();
  const tx = useT();
  const nav = useNavigate();
  const [seg, setSeg] = useState<Seg>('all');
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const all = useMemo(() => COSTING_JOBS.map(calcCosting), []);
  const segs: [Seg, string, number][] = [
    ['all', 'Tất cả', all.length],
    ['warn', 'Có cảnh báo', all.filter((c) => c.W.length).length],
    ['loss', 'Lỗ', all.filter(isLoss).length],
    ['late', 'Trễ duyệt', all.filter(isLate).length],
  ];
  const nq = norm(q.trim());
  const rows = all.filter((c) => (!nq || norm(`${c.job.no} ${c.job.cus}`).includes(nq)) && (seg === 'all' || (seg === 'warn' ? c.W.length > 0 : seg === 'late' ? isLate(c) : isLoss(c))));
  const noWrite = () => toast({ msg: 'Chưa mở ghi ở bản này' });
  const go = (no: string) => void nav({ to: '/ezy/cost/$job', params: { job: no } });

  return (
    <section className={s.card} aria-label="Costing theo job">
      <div className={s.bar}>
        <div className={s.segs} role="group" aria-label="Lọc costing">
          {segs.map(([k, label, n]) => (
            <button key={k} type="button" aria-pressed={seg === k} onClick={() => setSeg(k)}>
              {label}
              <span>{n}</span>
            </button>
          ))}
        </div>
        <span className={s.info} title={HINT_WARN} aria-label="Giải thích cảnh báo">
          i
        </span>
      </div>
      <table className={s.table} aria-label="Costing theo Job">
        <colgroup>
          {COLS.map((c) => (
            <col key={c.k} style={{ width: `${((c.w / TOTAL) * 100).toFixed(3)}%` }} />
          ))}
        </colgroup>
        <thead>
          <tr>
            {COLS.map((c, i) => (
              <th key={c.k} scope="col" className={[('right' in c ? s.right : ''), i === 0 ? s.first : ''].join(' ')}>
                {c.k}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((c) => {
            const j = c.job;
            const v = var_(c);
            const st = STATUS[j.s];
            const on = !!open[j.no];
            const pending = j.s === 'pend' && c.cost >= 200 && j.g[0] && j.g[1];
            return (
              <Fragment key={j.no}>
                <tr className={on ? s.rowOn : undefined} title={`Mở chi tiết costing ${j.no}`} onClick={() => go(j.no)}>
                  <td className={s.first}>
                    <div className={s.job}>
                      <button
                        type="button"
                        className={s.arrow}
                        aria-label="Bung theo dịch vụ"
                        aria-expanded={on}
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpen((p) => ({ ...p, [j.no]: !p[j.no] }));
                        }}
                      >
                        {on ? <IconChevronDown size={14} aria-hidden="true" /> : <IconChevronRight size={14} aria-hidden="true" />}
                      </button>
                      <div className={s.jobTx}>
                        <b className={s.no}>{j.no}</b>
                        <div className={s.sub}>{j.cus}</div>
                      </div>
                    </div>
                  </td>
                  <td className={s.right}>{f1(c.rev)}</td>
                  <td className={s.right}>{f1(c.qCost)}</td>
                  <td className={s.right}>{f1(c.cost)}</td>
                  <td className={`${s.right} ${s.b} ${varTone(v, 3)}`}>{signed(v)}</td>
                  <td className={`${s.right} ${s.b} ${c.gp < 0 ? s.err : s.ok}`}>
                    {f1(c.gp)}
                    <div className={s.sub}>{f1(c.mg)}%</div>
                  </td>
                  <td className={`${s.right} ${s.tx2}`}>{f1(c.gp - j.gk)}</td>
                  <td title={c.W.map((w) => w.k).join(' · ') || 'Không có cảnh báo'}>
                    <span className={s.chips}>
                      {c.wr > 0 && (
                        <span className={`${s.chip} ${s.cErr}`}>
                          <i className={s.dErr} />
                          {c.wr}
                        </span>
                      )}
                      {c.wo > 0 && (
                        <span className={`${s.chip} ${s.cWarn}`}>
                          <i className={s.dWarn} />
                          {c.wo}
                        </span>
                      )}
                      {!c.W.length && <span className={s.dOk} aria-label="Không có cảnh báo" />}
                      {isLate(c) && (
                        <span className={`${s.chip} ${c.deadline!.lv === 'r' ? s.cErr : s.cWarn}`} title={c.deadline!.tip}>
                          <i className={c.deadline!.lv === 'r' ? s.dErr : s.dWarn} />
                          {dlChip(c)}
                        </span>
                      )}
                    </span>
                  </td>
                  <td>
                    <div className={s.stat}>
                      <span
                        className={`${s.chip} ${s[`t_${st.tone}`]}`}
                        title={j.s === 'lockp' ? `Kỳ ${j.per} đã khoá · ACC-CA mở kỳ` : pending ? 'ACC-CP đã duyệt · chờ ACC-CA (chi ≥ 200 tr)' : st.k}
                      >
                        <i className={s[`d_${st.tone}`]} />
                        {st.k}
                      </span>
                      {j.s !== 'lockp' && (
                        <button
                          type="button"
                          className={s.act}
                          onClick={(e) => {
                            e.stopPropagation();
                            noWrite();
                          }}
                        >
                          {c.priLabel}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
                {on && (
                  <tr>
                    <td colSpan={COLS.length} className={s.exp}>
                      <div className={s.svc}>
                        {['Dịch vụ', 'Bán', 'Mua dự kiến', 'Mua thực tế', 'Chênh', 'Ghi chú'].map((h, i) => (
                          <span key={h} className={`${s.svcH} ${i > 0 && i < 5 ? s.right : ''}`}>
                            {h}
                          </span>
                        ))}
                        {c.svc.map((g) => {
                          const vv = g.act - g.exp;
                          return (
                            <Fragment key={g.k}>
                              <span>{g.k}</span>
                              <span className={s.right}>{f1(g.rev)}</span>
                              <span className={s.right}>{f1(g.exp)}</span>
                              <span className={s.right}>{f1(g.act)}</span>
                              <span className={`${s.right} ${varTone(vv, 2)}`}>{signed(vv)}</span>
                              <span className={s.note}>{g.notes.join(' · ') || '—'}</span>
                            </Fragment>
                          );
                        })}
                      </div>
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
      <div className={s.foot}>
        <span>{tx('fn.mTr')}</span>
        <span className={s.tools}>
          {rows.length} job
          <button type="button" onClick={() => toast({ msg: 'Đang tính lại costing các job chưa khoá · xong sẽ báo trong Việc' })}>
            <IconRefresh size={16} aria-hidden="true" />
            {tr('cjob.1')}
          </button>
          <button type="button" onClick={() => toast({ msg: 'Lợi nhuận job = debit bán − HĐ mua vào đã ghép − chi phí không HĐ · chi hộ không tính' })}>
            <IconHelp size={16} aria-hidden="true" />
            {tr('cjob.2')}
          </button>
          <button type="button" onClick={() => toast({ msg: 'Chia lợi nhuận chưa mở ở bản này' })}>
            {tr('cjob.3')}
            <IconArrowRight size={16} aria-hidden="true" />
          </button>
        </span>
      </div>
    </section>
  );
}

/** Tổng thanh của tab (board `TOTS.job`): chênh dự kiến · GP quản trị · số job có cảnh báo. */
export function jobTotals(): { varTotal: number; gp: number; warned: number } {
  const all = COSTING_JOBS.map(calcCosting);
  return { varTotal: all.reduce((a, c) => a + var_(c), 0), gp: all.reduce((a, c) => a + c.gp, 0), warned: all.filter((c) => c.W.length).length };
}
